// B2-4 (arc-new half) — reading the PATIENT'S OWN address off `profile.address`.
//
// ⭐ WHY THIS FILE EXISTS AT ALL. `Address` is declared in `src/contexts/types.ts` and carried on
// the profile, but before this change the only reader outside that file was
// `EnvironmentalDataManager.getCountryDetails`, which reads a *geocoded* country to pick the
// weather — not the patient's stored address. So the patient's own address was the one two-copy
// field their app held and never showed them. `MyProfile` already shows first_name, last_name,
// date_of_birth, existing_conditions and gender; address was the gap.
//
// ⛔ THIS IS THE PATIENT'S COPY AND NEVER THE ORGANIZATION'S. ACDPA-196 comment 13178 —
// "maintain 2 copies, no need to show the patient the data HME entered" — is a PROHIBITION, and
// the two copies live in different places: copy 1 is `profile.address` (this one, the patient's
// own row), copy 2 is the kernel's `patient_org_demographics` behind the org-scoped
// `/organizations/{id}/patients/*` routes. Nothing here may ever reach for an org-scoped
// endpoint. The kernel pins its half of the same rule with an AST test over its patient-facing
// modules' source (`test_me_never_reads_the_org_copy`); this comment plus `ownAddress.test.ts`
// is the arc-new half of it.
//
// ⚠️ B2-4 HAS NO USER NEED IN JIRA, AND THAT IS RECORDED RATHER THAN GLOSSED. c.13178 is only the
// prohibition; the positive half — "the patient can read their own copy" — is **D51 alone**, a
// standing rule given verbally on 2026-08-31. ⛔ Do not read a requirement into this code.
//
// ⛔ NO EDIT AFFORDANCE, AND THAT IS A BACKEND FACT RATHER THAN A UI CHOICE — see `MyProfile.tsx`
// where this is rendered. NEITHER backend accepts an address write today. Verified 2026-09-10:
//   * kernel `PATCH /me` — `ProfileUpdateRequest` (app/api/schemas/me.py) is
//     {first_name, last_name, date_of_birth, time_zone, user_name}. B2-4's own commit says it
//     deliberately added NO writable field: "adding `address` or `existing_conditions` to the
//     request body would make it worse".
//   * blaze `PUT /user/updateProfile` (api/controllers/UserController.js `basicFields`) — the
//     screen's existing writer. `address` is not in `basicFields` and is not handled anywhere
//     else in that action, so it is SILENTLY DROPPED and the call still answers 200
//     "Profile updated successfully".
// ⇒ wiring an input here would show the patient a save that persists nothing. Read-only is the
// honest rendering until one backend gains the field.

/** The patient's own address, exactly the six keys `contexts/types.ts` `Address` declares. */
export interface OwnAddress {
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  address_line_1?: string;
  address_line_2?: string;
}

/** The six wire keys, in the order a reader would name them. Not a display order — see `formatOwnAddress`. */
const PARTS: (keyof OwnAddress)[] = [
  "address_line_1",
  "address_line_2",
  "city",
  "state",
  "zip",
  "country",
];

/**
 * Coerce one stored part to a display string, or drop it.
 *
 * ⚠️ THIS DELIBERATELY DIVERGES FROM THE KERNEL, AND THE DIVERGENCE IS PINNED BY A NAMED TEST
 * (`ownAddress.test.ts` → "a numeric zip is shown, not dropped"). The kernel's `_self_address`
 * omits any part whose stored value is not a `str`; a NUMERIC ZIP in this blob is real — it is
 * what measurably raised a `ValidationError` on the kernel's hottest authenticated route and
 * forced that repair. Dropping it would hide a value the patient themselves entered, which is the
 * exact failure B2-4 exists to end. So finite numbers are rendered and everything else
 * (objects, arrays, booleans, NaN) is dropped.
 *
 * ⛔ If this screen's read is ever repointed at the kernel's `GET /me`, a numeric zip will vanish
 * with NO change to this file, because the kernel will have dropped it before the wire. That trap
 * is why the divergence is written down here instead of being left to be rediscovered.
 */
const part = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
};

/**
 * Read `profile.address` into a typed address, or `null` when there is nothing readable.
 *
 * ⛔ THE OBJECT IS GUARDED, NOT JUST ITS FIELDS. A blank-app defect was fixed in this repo on
 * `adfd422` where `getDisplayName` dereferenced a possibly-absent `profile`; because
 * `App.tsx`'s ErrorBoundary returns `null` (TD-165), ANY uncaught error blanks the WHOLE app on
 * web and native alike. `profile.address` is written by three applications and its column
 * server-defaults to `{}`, so `null`, `undefined`, `[]`, a string and a bare `{}` all reach here
 * in practice. Every one of them must answer `null` rather than throw.
 *
 * ⚠️ `{}` — the column's own server default — answers `null` (no address on file) rather than an
 * address of six blanks. The kernel measured the same thing and called the alternative "a blank
 * address FORM rather than 'no address on file'".
 */
export const readOwnAddress = (raw: unknown): OwnAddress | null => {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;

  const source = raw as Record<string, unknown>;
  const address: OwnAddress = {};
  let found = false;

  for (const key of PARTS) {
    const value = part(source[key]);
    if (value !== undefined) {
      address[key] = value;
      found = true;
    }
  }

  return found ? address : null;
};

/**
 * The patient's own address as display lines, empty when there is nothing to show.
 *
 * Four lines at most, each omitted when it would be blank:
 *   1. address_line_1
 *   2. address_line_2
 *   3. "city, state zip"  — the comma appears only when both sides of it are present
 *   4. country
 *
 * ⚠️ `country` is FREE TEXT here and is rendered exactly as stored. It is NOT the ISO
 * `country_lkup` code: that belongs to the ORGANIZATION's copy of the address
 * (`patient_org_demographics.country_code`, offered by the kernel's `GET /reference/countries`),
 * which this screen must never read. `MyAddress` on the kernel's `GET /me` is `country: str`
 * with no lookup validation, and TD-173 records that kernel, blaze and arc-new all still write
 * free text. ⇒ there is no ISO normalisation to do on this surface, and none is done.
 */
export const formatOwnAddress = (raw: unknown): string[] => {
  const address = readOwnAddress(raw);
  if (!address) return [];

  const locality = [
    [address.city, address.state].filter(Boolean).join(", "),
    address.zip,
  ]
    .filter(Boolean)
    .join(" ");

  return [
    address.address_line_1,
    address.address_line_2,
    locality,
    address.country,
  ].filter((line): line is string => Boolean(line));
};
